import React, { useState, useEffect } from "react";
import { burns as fallbackBurns, totalStats } from "../mock";
import { API, explorerTx, NET } from "../config";
import { WavyRule } from "./Guilloche";
import { ExternalLink } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("en-US");
const shortAddr = (a) => (a && a.length > 12 ? `${a.slice(0, 6)}\u2026${a.slice(-4)}` : a);
const ago = (iso) => {
  try {
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ""; }
};

export default function BurnHistory() {
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState({ total_destroyed: totalStats.totalDestroyed, certificates: totalStats.certificates });

  useEffect(() => {
    let alive = true;
    fetch(`${API}/certificates`).then((r) => r.json()).then((d) => {
      if (!alive) return;
      if (Array.isArray(d) && d.length) {
        setRows(d.map((c) => ({ when: ago(c.created_at), coin: c.token_symbol, destroyed: c.amount, burner: shortAddr(c.burner), cert: c.zcash_address, tx: c.tx_hash })));
      } else { setRows([]); }
    }).catch(() => setRows([]));
    fetch(`${API}/stats`).then((r) => r.json()).then((d) => { if (alive && d) setStats(d); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const data = rows && rows.length ? rows : fallbackBurns;
  const isLive = rows && rows.length > 0;

  return (
    <section id="burns" className="container-x py-10 md:py-14">
      <div className="paper paper-grain rounded-sm p-7 md:p-12">
        <h2 className="font-number font-semibold" style={{ fontSize: "clamp(1.6rem,3.4vw,2.3rem)" }}>Leaderboard</h2>
        <WavyRule className="my-6" />
        <p className="text-[15px] max-w-2xl" style={{ color: "var(--muted)" }}>
          {fmt(stats.total_destroyed)} tokens destroyed across {stats.certificates} certificate{stats.certificates === 1 ? "" : "s"}. {isLive ? "Live from this deployment." : "Showing sample records until the first real burn is issued."} Read from {NET.name}; a refused burn still destroyed its tokens.
        </p>

        <div className="mt-8 rounded-sm overflow-x-auto" style={{ border: "1px solid var(--border)" }}>
          <table className="w-full text-left" style={{ minWidth: 720 }}>
            <thead>
              <tr className="label" style={{ background: "var(--bg-2)" }}>
                <th className="px-5 py-3">When</th>
                <th className="px-5 py-3">Coin</th>
                <th className="px-5 py-3">Destroyed</th>
                <th className="px-5 py-3">Burner</th>
                <th className="px-5 py-3">Certificate to</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.map((b, i) => (
                <tr key={i} style={{ borderTop: "1px solid var(--border)" }} className="transition-colors"
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--card-2)")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                  <td className="px-5 py-4 text-[13px]" style={{ color: "var(--muted)" }}>{b.when}</td>
                  <td className="px-5 py-4 text-[13px] font-mono font-semibold gold-text">${b.coin}</td>
                  <td className="px-5 py-4 text-[13px] font-mono">{fmt(b.destroyed)}</td>
                  <td className="px-5 py-4 text-[12px] font-mono" style={{ color: "var(--muted)" }}>{b.burner}</td>
                  <td className="px-5 py-4 text-[12px] font-mono truncate" style={{ maxWidth: 220 }}>{b.cert}</td>
                  <td className="px-5 py-4">
                    <a href={explorerTx(b.tx)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] gold-text link-underline">tx <ExternalLink size={12} /></a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
